import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TabTitle } from "../../../util/titleChanger";
import { GlobalState } from "../../../GlobalState";
import { deleteInvoice, getInvoicesByDateRange } from "../../../action";
import { toast, ToastContainer } from "react-toastify";
import Loading from "../../../util/Loading";
import ReactPaginate from "react-paginate";

export default function InvoiceDashboard() {
  TabTitle("Invoice Dashboard");
  const state = useContext(GlobalState);
  const signInUser = JSON.parse(localStorage.getItem("user_data"));

  const [invoiceData, setInvoiceData] = useState([]);
  const [printInvoice, setPrintInvoice] = useState(null); // ✅ For handling print

  const [invoices] = state.invoiceAPI.invoices;
  const [rowPerPage] = state.invoiceAPI.rowPerPage;
  const [pageNumber, setPageNumber] = state.invoiceAPI.pageNumber;
  const [totalPages] = state.invoiceAPI.totalPages;
  const [setSearch] = state.invoiceAPI.search;
  const [callback, setCallback] = state.invoiceAPI.callback;

  let gotDate = new Date();
  let defaultDate = new Date(gotDate).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(defaultDate);
  const [endDate, setEndDate] = useState(defaultDate);
  const [trigger, setTrigger] = useState(false);

  const pageNumberHandler = (data) => setPageNumber(data.selected + 1);

  const searchHandler = (e) => {
    e.preventDefault();
    setPageNumber(1);
    setSearch(e.target.value || "");
  };

  const searchByDateHandler = (e) => {
    e.preventDefault();
    setPageNumber(1);
    setTimeout(() => setTrigger(true), 1000);
    e.target.name === "start_date" ? setStartDate(e.target.value) : setEndDate(e.target.value);
  };

  const onChangeSearchHandler = (start_date, end_date) => {
    getInvoicesByDateRange(rowPerPage, pageNumber, start_date, end_date, (res) => {
      setInvoiceData(res);
      setPageNumber(res.totalPages || 1);
    });
  };

  const deleteInvoiceHandler = (id) => {
    deleteInvoice(id, signInUser.id, (res) => {
      toast[res.status === 200 ? "success" : "error"](res.data);
      setCallback(!callback);
    });
  };

  useEffect(() => {
    setInvoiceData(invoices);
    if (trigger) {
      onChangeSearchHandler(startDate, endDate);
      setTrigger(false);
    }
  }, [rowPerPage, pageNumber, invoices, trigger]);

  // ✅ Print Styles
  const getReceiptStyles = () => `
    @page {
      size: 80mm auto;
      margin: 0;
    }
    body {
      width: 80mm !important;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      padding: 5mm;
      background: white;
      color: black;
    }
    .receipt {
      width: 100%;
    }
    .header, .footer {
      text-align: center;
      font-weight: bold;
      margin: 10px 0;
    }
    .divider {
      border-top: 1px dashed #000;
      margin: 5px 0;
    }
    .item-row {
      display: flex;
      justify-content: space-between;
      margin: 3px 0;
    }
    .total {
      font-weight: bold;
    }
  `;

  // ✅ Receipt Generator
  const generateReceiptHTML = (invoice) => `
    <div class="receipt">
      <div class="header">
        <h3>BUY ME NOW</h3>
        <p>Invoice #${invoice.invoice_no}</p>
        <p>Date: ${new Date(invoice.created_at).toLocaleString()}</p>
        <p>Customer: ${invoice.customer?.first_name || ""} ${invoice.customer?.last_name || ""}</p>
      </div>
      <div class="divider"></div>
      ${invoice.bills
        ?.map(
          (item) => `
        <div class="item-row">
          <span>${item.quantity}x ${item.product.name}</span>
          <span>Rs ${parseFloat(item.total_price).toFixed(2)}</span>
        </div>`
        )
        .join("")}
      <div class="divider"></div>
      <div class="item-row total">
        <span>Total:</span>
        <span>Rs ${parseFloat(invoice.net_price).toFixed(2)}</span>
      </div>
      <div class="footer">Thank you for shopping!</div>
    </div>
  `;

  // ✅ Print Trigger
  const handlePrint = (invoice) => {
    const content = `
      <html>
        <head>
          <title>Invoice #${invoice.invoice_no}</title>
          <style>${getReceiptStyles()}</style>
        </head>
        <body>
          ${generateReceiptHTML(invoice)}
          <script>
            setTimeout(() => window.print(), 100);
          </script>
        </body>
      </html>
    `;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(content);
    printWindow.document.close();
    toast.success("Print window opened");
  };

  return (
    <div className="form-container">
      <div className="table">
        <div className="table_header">
          <p>Invoice Details</p>
          <div className="custom-search">
            <input type="date" name="start_date" value={startDate} onChange={searchByDateHandler} />
            <input type="date" name="end_date" value={endDate} onChange={searchByDateHandler} />
            <input type="text" name="search" placeholder="Search purchase here..." onChange={searchHandler} />
            <Link to="/billing">
              <button className="add_new">+ Add New</button>
            </Link>
          </div>
        </div>

        {invoiceData.count === 0 ? (
          <div className="loading-page-container">
            <Loading />
          </div>
        ) : (
          <div className="table_section">
            <table>
              <thead>
                <tr>
                  <th>Invoice No</th>
                  <th>Customer Name</th>
                  <th>Total Price</th>
                  <th>Total Discount</th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {invoiceData.count > 0 ? (
                  invoiceData.data.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>{invoice.invoice_no}</td>
                      <td>{invoice.customer.first_name + " " + invoice.customer.last_name}</td>
                      <td>Rs. {invoice.total_price}</td>
                      <td>Rs. {invoice.total_discount}</td>
                      <td>Rs. {invoice.net_price}</td>
                      <td>
                        <Link to={`/invoice-view/${invoice.id}`}>
                          <button>
                            <i className="uil uil-eye"></i>
                          </button>
                        </Link>
                        <button onClick={() => deleteInvoiceHandler(invoice.id)}>
                          <i className="uil uil-trash-alt"></i>
                        </button>
                        {/* ✅ Print button */}
                        <button onClick={() => handlePrint(invoice)}>
                          <i className="uil uil-print"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>Can not found any purchase details.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <ReactPaginate
          breakLabel="..."
          nextLabel="next >"
          onPageChange={pageNumberHandler}
          marginPagesDisplayed={2}
          pageCount={totalPages}
          previousLabel="< previous"
          renderOnZeroPageCount={null}
          containerClassName="pagination"
          pageLinkClassName="page-num"
          previousLinkClassName="page-num"
          nextLinkClassName="page-num"
          activeLinkClassName="active-num"
        />
      </div>
      <ToastContainer />
    </div>
  );
}
